/**
 * Provides end points to track various analytics events.
 * Binds incoming info (cart / product) then publishes the event to listeners (in ./triggers).
 */
import { getCart } from "../../components/Cart/cartRepo";
import {
    triggerAddToCartEvent,
    triggerBeginCheckoutEvent,
    triggerButtonClickEvent,
    triggerCheckoutCompleteEvent,
    triggerPWAInstalledEvent,
    triggerPWAInstallPromptedEvent,
    triggerViewItemEvent,
} from "./triggers";

import {
    getProductFromPage,
    getQuantityFromPage,
    validateAttributeSelection,
} from "./page-elements";
import { ButtonClickObject, GoogleOrder, HgsCart, HgsCartItem, HgsProductItem } from "./types";

//  Track Google Analytics view_item event
export function trackViewItemEvent(itemsObject: HgsProductItem) {
    window.addEventListener("DOMContentLoaded", function () {
        triggerViewItemEvent(itemsObject);
    });
}

// Track Google Analytics add_to_cart event

export function trackAddToCartEvent(itemsObject: HgsProductItem) {
    const addtoCartButton = document.querySelector("[id*=btnAddToCart]");

    addtoCartButton?.addEventListener("click", function () {
        const attributeIsValid = validateAttributeSelection();
        if (!attributeIsValid) {
            return;
        }

        const quantityNumber = getQuantityFromPage();

        const itemsObjectWithQuantity: HgsCartItem = {
            ...itemsObject,
            quantity: quantityNumber,
        };

        triggerAddToCartEvent(itemsObjectWithQuantity);
    });
}

//Track Button Click Events
export function trackButtonClickEvent(element: HTMLButtonElement) {
    element?.addEventListener("click", function (event) {
        const { target } = event;

        if (target instanceof HTMLButtonElement) {
            const { id, innerText } = target;

            const buttonClickedObject: ButtonClickObject = {
                button_id: id,
                button_text: innerText,
                page_location: window.location.href,
            };
            triggerButtonClickEvent(buttonClickedObject);
        }
    });
}

//Track PWA Install Prompt Being shown
export function trackPWAInstallPrompt() {
    window.addEventListener("beforeinstallprompt", () => {
        triggerPWAInstallPromptedEvent();
    });
}

//Track PWA Installation
export function trackPWAInstallation() {
    window.addEventListener("appinstalled", () => {
        if (document.visibilityState !== "visible") return;
        triggerPWAInstalledEvent();
    });
}

async function createAnalyticsBeginCheckoutObject(): Promise<HgsCart> {
    try {
        const cart = await getCart();
        const itemsArray: HgsCartItem[] = cart.Items.map((item) => {
            return {
                item_id: item.ProductCode.trim(),
                item_name: item.ProductName.trim(),
                price: item.Price,
                quantity: item.Quantity,
                description: "",
                image: ""
            };
        });

        const cartObject: HgsCart = {
            total_price: cart.MerchandiseTotal,
            items: itemsArray,
        };

        return cartObject;
    } catch (error) {
        console.error("Error creating cart object", error);
        throw error;
    }
}

export async function trackBeginCheckout(): Promise<void> {
    try {
        const beginCheckoutObject = await createAnalyticsBeginCheckoutObject();
        triggerBeginCheckoutEvent(beginCheckoutObject);
    } catch (error) {
        console.error("Error tracking begin_checkout", error);
        throw error;
    }
}

export function trackCheckoutComplete(order: GoogleOrder): void {
    triggerCheckoutCompleteEvent(order);
}
